import { SEARCH_FILTERABLE_FIELDS, SortOrder } from '@telialt/core-search/src/constants';
import { SearchFilters } from '@telialt/core-search/src/model';
import { extractANDFilters } from '@telialt/core-search/src/utils/filtersUtils';

import { BlockProductsType } from '../../models/Content';
import { selectProductsQueryKey } from '../../utils/contentSelectors';
import { transformCta } from '../../utils/transformers';
import { BlockProductsData } from './model';

const toTextValues = (values: unknown): string[] =>
    Array.isArray(values)
        ? values.filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        : [];

const mergeFilterValues = (filters: SearchFilters, filterField: string, kind: string, values: string[]) => {
    if (kind === 'hierarchy') {
        const hierarchyFilter = filters[filterField];
        const hierarchyTerms =
            hierarchyFilter && !Array.isArray(hierarchyFilter) && 'terms' in hierarchyFilter
                ? hierarchyFilter.terms
                : [];

        filters[filterField] = { level: 0, terms: [...hierarchyTerms, ...values] };

        return;
    }

    const termsFilter = filters[filterField];

    filters[filterField] = [...(Array.isArray(termsFilter) ? termsFilter : []), ...values];
};

const toSearchFilters = (
    entries: NonNullable<BlockProductsType['fields']['productsQuery']>['fields']['searchFilters'],
): SearchFilters | undefined => {
    if (!entries?.length) {
        return undefined;
    }

    const filters = entries.reduce<SearchFilters>((acc, entry) => {
        const filterField = entry?.fields?.filterField;
        const kind = filterField ? SEARCH_FILTERABLE_FIELDS[filterField] : undefined;

        if (!filterField || !kind || !entry?.fields) {
            return acc;
        }

        if (kind === 'range') {
            const { gte, lte } = entry.fields;

            if (typeof gte === 'number' && typeof lte === 'number') {
                acc[filterField] = { gte, lte };
            }

            return acc;
        }

        const values = toTextValues(entry.fields.values);

        if (!values.length) {
            return acc;
        }

        mergeFilterValues(acc, filterField, kind, values);

        return acc;
    }, {});

    return Object.keys(filters).length ? extractANDFilters(filters) : undefined;
};

const toProductLimit = (limit: unknown): number | undefined =>
    typeof limit === 'number' && Number.isInteger(limit) && limit >= 1 ? limit : undefined;

const PRODUCT_SORTS: Record<string, NonNullable<BlockProductsData['sort']>> = {
    'Name, A to Z': [{ name: SortOrder.Asc }],
    'Name, Z to A': [{ name: SortOrder.Desc }],
    'Price, low to high': [{ fullPrice: SortOrder.Asc }],
    'Price, high to low': [{ fullPrice: SortOrder.Desc }],
    'Monthly price, low to high': [{ monthlyPrice: SortOrder.Asc }],
    'Monthly price, high to low': [{ monthlyPrice: SortOrder.Desc }],
    Newest: [{ effectiveDate: SortOrder.Desc }],
    Oldest: [{ effectiveDate: SortOrder.Asc }],
    'Clicks, high to low': [{ _clickCount: SortOrder.Desc }],
    'Clicks, low to high': [{ _clickCount: SortOrder.Asc }],
    'Relevance, high to low': [{ _relevance: SortOrder.Desc }],
    'Relevance, low to high': [{ _relevance: SortOrder.Asc }],
};

const toProductSort = (sort: unknown): BlockProductsData['sort'] =>
    typeof sort === 'string' ? PRODUCT_SORTS[sort] : undefined;

export const transformBlockProducts = (blockProducts: BlockProductsType): BlockProductsData => {
    if (!blockProducts) {
        return undefined;
    }

    const queryKey = selectProductsQueryKey(blockProducts)?.trim() || undefined;

    return {
        id: blockProducts.sys.id,
        blockTitle: blockProducts.fields.title,
        blockCta: transformCta(blockProducts.fields.cta),
        queryKey,
        searchFilters: toSearchFilters(blockProducts.fields.productsQuery?.fields?.searchFilters),
        sort: toProductSort(blockProducts.fields.productsQuery?.fields?.sort),
        limit: toProductLimit(blockProducts.fields.productsQuery?.fields?.limit),
    };
};
